import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Menu, X, Phone, Mail, MapPin, ChevronDown } from 'lucide-react';
import { cn } from '../lib/utils';

const navItems = [
  { label: 'Forside', href: '/' },
  { label: 'Om os', href: '/om-os' },
  { 
    label: 'Vi tilbyder', 
    href: '#',
    children: [
      { label: 'Kloakmesterarbejde', href: '/kloakmester-arbejde' },
      { label: 'Entreprenørarbejde', href: '/entreprenoerarbejde' },
      { label: 'Kloakservice', href: '/kloakservice' },
      { label: 'Kloakseparering', href: '/kloakseparering' },
      { label: 'TV-inspektion', href: '/tv-inspektion' },
      { label: 'Anlæg og belægning', href: '/anlaeg-og-belaegning' },
      { label: 'Støbning af beton', href: '/stoebning-af-beton' },
      { label: 'Haveservice', href: '/haveservice' },
    ]
  },
  { label: 'Galleri', href: '/galleri' },
  { label: 'Ledige stillinger', href: '/ledige-stillinger' },
  { label: 'Kontakt', href: '/kontakt' },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const isActive = (href) => {
    if (!href || href === '#') return false;
    const [pathAndHash, query] = href.split('?');
    const path = pathAndHash.split('#')[0];
    if (location.pathname !== path) return false;
    if (!query) return true;
    return query.split('&').every((pair) => location.search.includes(pair));
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <nav className={cn(
      "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
      scrolled ? "bg-white shadow-md py-2" : "bg-transparent py-4"
    )}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3 group">
            <img 
              src="https://assets.auxodev.dk/cdn-cgi/image/width=240,quality=90,format=auto/assets/6015610c-faa3-4021-bc7e-a866f30ca512/021a31cb-25cf-47cf-a88c-008b05293be5.png" 
              alt="Entreprenør Jan Hedegaard logo - Autoriseret kloakmester i Galten" 
              className={cn(
                "h-16 lg:h-[120px] w-auto transition-all duration-300 group-hover:scale-110",
                !scrolled && "brightness-0 invert"
              )}
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden min-[1200px]:flex items-center space-x-6">
            {navItems.map((item) => (
              <div key={item.label} className="relative group">
                <Link
                  to={item.href}
                  className={cn(
                    "text-sm font-medium transition-colors flex items-center gap-1 py-2",
                    scrolled ? "text-stone-600 hover:text-orange-600" : "text-stone-100 hover:text-orange-400",
                    location.pathname === item.href && "text-orange-500 font-bold"
                  )}
                >
                  {item.label}
                  {item.children && <ChevronDown className="w-4 h-4" />}
                </Link>
                
                {item.children && (
                  <div className="absolute top-full left-0 mt-0 w-56 bg-white shadow-xl rounded-b-lg py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 border-t-2 border-orange-600">
                    {item.children.map((child) => (
                      <Link
                        key={child.label}
                        to={child.href}
                        className={cn(
                          "block px-4 py-2 text-sm transition-colors",
                          location.pathname === child.href 
                            ? "bg-orange-50 text-orange-600 font-semibold" 
                            : "text-stone-600 hover:bg-stone-50 hover:text-orange-600"
                        )}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <Link
              to="/kontakt"
              className="bg-orange-600 text-white px-5 py-2.5 rounded-md text-sm font-semibold hover:bg-orange-700 transition-all hover:scale-105 shadow-lg shadow-orange-900/20"
            >
              Få et tilbud
            </Link>
          </div>

          {/* Mobile Toggle */}
          <button
            className={cn(
              "min-[1200px]:hidden p-2 rounded-md",
              scrolled ? "text-stone-900" : "text-white"
            )}
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      <div className={cn(
        "fixed inset-0 z-40 bg-white min-[1200px]:hidden transition-transform duration-300 ease-in-out transform overflow-y-auto",
        isOpen ? "translate-x-0" : "translate-x-full"
      )}>
        <button
          onClick={() => setIsOpen(false)}
          className="absolute top-4 right-4 p-2 text-stone-900 hover:text-orange-600 transition-colors"
          aria-label="Luk menu"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="pt-20 pb-6 px-4 space-y-4">
          {navItems.map((item) => {
            const active = isActive(item.href);
            return (
              <div key={item.label} className="space-y-2">
                <Link
                  to={item.href}
                  className={cn(
                    "block text-lg font-semibold px-4 py-2 transition-colors",
                    active ? "text-orange-600" : "text-stone-900"
                  )}
                >
                  {item.label}
                </Link>
                {item.children && (
                  <div className="pl-8 space-y-2">
                    {item.children.map((child) => {
                      const childActive = isActive(child.href);
                      return (
                        <Link
                          key={child.label}
                          to={child.href}
                          className={cn(
                            "block py-1 transition-colors",
                            childActive ? "text-orange-600 font-semibold" : "text-stone-600"
                          )}
                        >
                          {child.label}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          <div className="pt-6 border-t border-stone-100 px-4 space-y-4">
            <div className="flex items-center gap-3 text-stone-600">
              <Phone className="w-5 h-5 text-orange-600" />
              <span className="font-medium">22 45 85 67</span>
            </div>
            <div className="flex items-center gap-3 text-stone-600">
              <Mail className="w-5 h-5 text-orange-600" />
              <span className="font-medium">jan@janhedegaard.dk</span>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
